import { useCallback, useEffect, useMemo, useRef, useState, startTransition } from "react";
import axios from "axios";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faExclamationTriangle,
} from "@awesome.me/kit-70c1f0055f/icons/classic/solid";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button/Button";
import { Badge } from "@/components/ui/badge/Badge";
import LoadingPlaceholder from "@/components/ui/loading-placeholder/LoadingPlaceholder";
import {
  useOsActionsOptional,
  useOsNavigate,
  useSetWindowTitle,
} from "@/components/ui/os-layout";
import {
  WORKBENCH_ROOT_TEXT_CLASS,
  WORKBENCH_SURFACE_CLASS,
} from "@/components/ui/workbench";
import * as AgenticV2API from "@/services/api/agentic-v2";
import type {
  Artifact,
  FaClientDetail,
  AgentSummary,
  MissionSummary,
  TaskActionItem,
  VisibilityScope,
} from "@/services/api/agentic-v2";
import type {
  FaClientDataGathering,
  PlanDataGatheringStatus,
} from "@/services/api/agentic-v2/types";
import { VisibilityFilter } from "@/components/agentic-v2/VisibilityFilter";
import { SegmentedPillToggle, SegmentedTabs } from "@/components/ui/segmented-tabs";
import { MissionsVirtualList } from "@/components/missions/MissionsVirtualList";
import { useAgentsQuery } from "@/services/api/agentic-v2/queries/useAgentsQuery";
import { useTaskPalette } from "@/context/TaskPaletteContext";
import { buildMissionDetailWindowUrl } from "@/navigation/apps/missionDetailLocation";
import { ClientTasksKanban } from "./ClientTasksKanban";
import { DataGatheringPlanProgress } from "./DataGatheringPlansBanner";
import { DataGatheringAgentSummary } from "./DataGatheringAgentSummary";
import { TaskThreadDrawer } from "@/components/missions/detail/TaskThreadDrawer";
import { ClientAvatar } from "./ClientAvatar";
import { ClientInsightCard } from "./ClientInsightCard";
import { ClientTimeline } from "./ClientTimeline";
import { Accordion } from "@/components/ui/accordion";
import { useOpenWindow } from "@/components/ui/os-layout/useWindow";
import { useVirtualScrollElement } from "@/components/mission-control/useVirtualScrollElement";
import { useWindowLive } from "@/components/ui/os-layout/WindowActiveContext";
import { useAgenticSubscription } from "@/hooks/useAgenticSubscription";
import { clientsKeys, missionControlKeys } from "@/services/api/agentic-v2/queries/keys";
import {
  createClientDetailWsSubscription,
  createDebouncedClientDetailInvalidate,
} from "@/services/api/agentic-v2/queries/useClientDetailWs";
import { useModulePermissions } from "@/context/ModulePermissionsContext";
import { useClientDetail } from "@/services/api/agentic-v2/queries/useClientDetail";
import { useClientMissions } from "@/services/api/agentic-v2/queries/useClientMissions";
import { useClientTasks } from "@/services/api/agentic-v2/queries/useClientTasks";
import { useClientTimeline } from "@/services/api/agentic-v2/queries/useClientTimeline";
import { useClientArtifacts } from "@/services/api/agentic-v2/queries/useClientArtifacts";
import { useClientDataGathering } from "@/services/api/agentic-v2/queries/useClientDataGathering";
import { ClientArtifactsSection } from "@/components/clients/ClientArtifactsSection";
import { DataGatheringClientPlansPanel } from "@/components/clients/data-gathering/DataGatheringClientPlansPanel";
import { type ClientDetailTabId } from "@/components/clients/clientDetailTabs";
import { taskConsoleStatus } from "@/components/agentic-surface/widgets/home/needsYouTableModel";
import { TaskReviewTable } from "./TaskReviewTable";
import { useAppLocation } from "@/navigation/appLocation";
import { clientDetailCodec } from "@/navigation/apps/clientDetailLocation";
import {
  compactJourneyStageLabel,
  journeyStageBadgeColor,
  journeyStageStatusDotColor,
  shortJourneyStageLabel,
} from "@/components/clients/clientStageDisplay";
import { StatusDot } from "@/components/ui/status-dot";
import { PlanArtifactActions } from "./PlanArtifactActions";
import { ClientCalculatorLinks } from "./ClientCalculatorLinks";
import { ClientNarrativeSourcePanel } from "./ClientNarrativeSourcePanel";
import {
  buildPlanArtifactIndex,
  buildArtifactById,
  resolvePlanCheatSheetArtifact,
  formatPlanValue,
  sumPlanValues,
  groupPlansByCategory,
  isHouseholdClient,
  memberDisplayName,
  memberNamesLine,
  planOwnersLabel,
  renderPlanField,
  showPlanLoaStatus,
  type ClientPlanRow,
  type PlanArtifactLinks,
  type PlanCategory,
} from "./clientDisplay";

/**
 * Per-client record.
 *
 * Header + segmented tabs:
 *   Overview | Tasks | Plans | Calculators | Artefacts | Activity
 *
 * Tasks has three views: Needs you (review table), All tasks (kanban + task thread), Missions.
 * Plans splits into Checklist / Plans while the client is in data gathering.
 */
export interface ClientDetailProps {
  faClientId: string;
}

type TabId = ClientDetailTabId;

type TaskView = "needs_you" | "all" | "missions";

const TASK_VIEW_OPTIONS: { id: TaskView; label: string }[] = [
  { id: "needs_you", label: "Needs you" },
  { id: "all", label: "All tasks" },
  { id: "missions", label: "Missions" },
];

type PlansView = "checklist" | "plans";

const PLANS_VIEW_OPTIONS: { id: PlansView; label: string }[] = [
  { id: "checklist", label: "Checklist" },
  { id: "plans", label: "Plans" },
];

function needsHuman(task: TaskActionItem): boolean {
  const status = taskConsoleStatus(task);
  return status === "needs_you" || status === "blocked";
}

/** Resolved task-palette inputs when delegating from a plan row. */
type DelegatedTaskInputs = Record<string, string | number | boolean | undefined>;

export function ClientDetail({ faClientId }: ClientDetailProps) {
  const setWindowTitle = useSetWindowTitle();
  const osNavigate = useOsNavigate();
  const osActions = useOsActionsOptional();
  const queryClient = useQueryClient();
  const isWindowLive = useWindowLive();

  const [location, setLocation] = useAppLocation(clientDetailCodec);
  const [taskVisibility, setTaskVisibility] = useState<VisibilityScope>("all");

  const { client, loading, error, refresh } = useClientDetail({ faClientId });
  const { summary: dgSummary } = useClientDataGathering({
    faClientId,
    enabled: isWindowLive && Boolean(client),
  });
  const inDgPhase = dgSummary?.in_data_gathering_phase ?? false;
  const { hasPermission } = useModulePermissions();
  const showCalculatorsTab = hasPermission("calculators");
  const visibleTab: TabId = location.tab === "calculators" && !showCalculatorsTab ? "overview" : location.tab ?? "overview";
  const selectedTaskId = location.taskId ?? null;
  const [taskView, setTaskView] = useState<TaskView>(selectedTaskId ? "all" : "needs_you");
  const [plansView, setPlansView] = useState<PlansView>("checklist");
  const threadPaneTab = (visibleTab === "tasks" && taskView !== "missions") || visibleTab === "plans";
  const taskThreadOpen = selectedTaskId !== null && threadPaneTab;

  const missions = useClientMissions({
    faClientId,
    enabled: isWindowLive,
  });
  const tasks = useClientTasks({
    faClientId,
    visibility: taskVisibility,
    enabled: isWindowLive,
  });
  const needsYouTasks = useMemo(() => tasks.tasks.filter(needsHuman), [tasks.tasks]);
  const timeline = useClientTimeline({
    faClientId,
    enabled: visibleTab === "activity",
  });
  const agents = useAgentsQuery({
    enabled: isWindowLive && visibleTab === "tasks" && taskView === "missions",
  });

  const narrativeMissionId = useMemo(() => {
    if (selectedTaskId) {
      const task = tasks.tasks.find((t) => t._id === selectedTaskId);
      const fromTask = task?.mission?.id;
      if (fromTask) return fromTask;
    }
    const activeMission =
      missions.missions.find((m) => m.status === "active") ?? missions.missions[0];
    return activeMission?._id ?? null;
  }, [missions.missions, selectedTaskId, tasks.tasks]);

  const tasksKeyInput = tasks.keyInput;
  const debouncedInvalidateDetail = useMemo(
    () => createDebouncedClientDetailInvalidate(queryClient, faClientId),
    [queryClient, faClientId],
  );

  useEffect(
    () => () => debouncedInvalidateDetail.cancel(),
    [debouncedInvalidateDetail],
  );

  const clientWsSubscription = useMemo(
    () =>
      createClientDetailWsSubscription(
        queryClient,
        faClientId,
        tasksKeyInput,
        debouncedInvalidateDetail.invalidate,
      ),
    [queryClient, faClientId, tasksKeyInput, debouncedInvalidateDetail],
  );

  useAgenticSubscription(clientWsSubscription);

  const selectTask = useCallback(
    (taskId: string) => {
      void queryClient.prefetchQuery({
        queryKey: missionControlKeys.task(taskId),
        queryFn: () => AgenticV2API.getTask(taskId),
      });
      void queryClient.prefetchQuery({
        queryKey: missionControlKeys.taskThread(taskId),
        queryFn: () => AgenticV2API.getTaskThread(taskId),
      });
      startTransition(() => setLocation({ taskId }));
    },
    [queryClient, setLocation],
  );

  const closeTask = useCallback(() => {
    startTransition(() => setLocation({ taskId: undefined }));
  }, [setLocation]);

  const staleTaskProbe = useQuery({
    queryKey: missionControlKeys.task(selectedTaskId ?? ""),
    enabled: Boolean(selectedTaskId) && taskThreadOpen && isWindowLive,
    retry: false,
    queryFn: () => AgenticV2API.getTask(selectedTaskId!),
  });

  useEffect(() => {
    const err = staleTaskProbe.error;
    if (!selectedTaskId || !err) return;
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      closeTask();
    }
  }, [closeTask, selectedTaskId, staleTaskProbe.error]);

  const invalidateClientData = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: [...clientsKeys.all, "detail", faClientId],
    });
    void queryClient.invalidateQueries({
      queryKey: [...clientsKeys.all, "missions", faClientId],
    });
    void queryClient.invalidateQueries({ queryKey: clientsKeys.tasks(tasksKeyInput) });
    void queryClient.invalidateQueries({
      queryKey: [...clientsKeys.all, "timeline", faClientId],
    });
    void queryClient.invalidateQueries({
      queryKey: [...clientsKeys.all, "artifacts", faClientId],
    });
    void queryClient.invalidateQueries({
      queryKey: [...clientsKeys.all, "data-gathering", faClientId],
    });
  }, [queryClient, faClientId, tasksKeyInput]);

  const palette = useTaskPalette();

  const titleSetRef = useRef(false);
  useEffect(() => {
    if (titleSetRef.current || !client) return;
    setWindowTitle(client.display_name);
    titleSetRef.current = true;
  }, [client, setWindowTitle]);

  const navigateToMission = (mission: MissionSummary) => {
    if (osActions) {
      osActions.openWindow({
        appId: "mission",
        title: mission.template_name ?? "Mission",
        params: { missionId: mission._id },
      });
      return;
    }
    osNavigate(buildMissionDetailWindowUrl(mission._id));
  };

  const openMissionById = (missionId: string) => {
    if (osActions) {
      osActions.openWindow({
        appId: "mission",
        title: "Mission",
        params: { missionId },
      });
      return;
    }
    osNavigate(buildMissionDetailWindowUrl(missionId));
  };

  const onTaskLaunched = (taskId: string) => {
    void queryClient.prefetchQuery({
      queryKey: missionControlKeys.task(taskId),
      queryFn: () => AgenticV2API.getTask(taskId),
    });
    void queryClient.prefetchQuery({
      queryKey: missionControlKeys.taskThread(taskId),
      queryFn: () => AgenticV2API.getTaskThread(taskId),
    });
    setTaskView("all");
    startTransition(() => setLocation({ tab: "tasks", taskId }));
    invalidateClientData();
  };

  const delegatePlanTask = (
    catalogueId: string,
    inputsResolved: DelegatedTaskInputs,
  ) => {
    palette.open({
      initialFaClientId: faClientId,
      initialCatalogueId: catalogueId,
      initialInputsResolved: inputsResolved,
      onLaunched: (taskId) => onTaskLaunched(taskId),
    });
  };

  const tabs = useMemo(() => {
    const items = [
      { id: "overview", label: "Overview" },
      { id: "tasks", label: "Tasks", count: needsYouTasks.length || undefined, countVariant: "attention" as const },
      { id: "plans", label: "Plans", count: (client?.plans?.length as number | undefined) || undefined },
    ];
    if (showCalculatorsTab) items.push({ id: "calculators", label: "Calculators" });
    items.push({ id: "artifacts", label: "Artefacts" }, { id: "activity", label: "Activity" });
    return items;
  }, [needsYouTasks.length, client?.plans?.length, showCalculatorsTab]);

  const openTasks = (view: TaskView) => {
    setTaskView(view);
    setLocation({ tab: "tasks" });
  };

  return (
    <div
      className={clsx("flex h-full w-full p-4 gap-4", WORKBENCH_ROOT_TEXT_CLASS)}
      data-testid="client-detail"
    >
      <div
        className={clsx(
          "flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden rounded-2xl",
          WORKBENCH_SURFACE_CLASS,
          taskThreadOpen && "[contain:layout]",
        )}
      >
        {loading ? (
          <div className="p-6">
            <LoadingPlaceholder height="120px" />
          </div>
        ) : error || !client ? (
          <ErrorState
            message={error?.message ?? "Client not found"}
            onRetry={refresh}
          />
        ) : (
          <>
            <div className="px-6 pt-5 pb-4">
              <Header
                client={client}
                onNewTask={() =>
                  palette.open({
                    initialFaClientId: faClientId,
                    onLaunched: (taskId) => onTaskLaunched(taskId),
                  })
                }
              />
            </div>

            <div className="px-6 overflow-x-auto">
              <SegmentedTabs
                tabs={tabs}
                activeId={visibleTab}
                onChange={(id) => setLocation({ tab: id as TabId })}
              />
            </div>

            <div
              className={clsx(
                "flex-1 min-h-0 px-6 py-5",
                visibleTab === "tasks" ? "flex flex-col overflow-hidden" : "overflow-y-auto",
              )}
            >
              {visibleTab === "overview" && (
                <OverviewTab
                  client={client}
                  faClientId={faClientId}
                  missionId={narrativeMissionId}
                  needsYouCount={needsYouTasks.length}
                  tasksLoading={tasks.loading}
                  showCalculators={showCalculatorsTab}
                  onReviewTasks={() => openTasks("needs_you")}
                />
              )}
              {visibleTab === "tasks" && (
                <TasksTab
                  view={taskView}
                  onViewChange={setTaskView}
                  tasks={tasks.tasks}
                  needsYouTasks={needsYouTasks}
                  loading={tasks.loading}
                  visibility={taskVisibility}
                  onVisibilityChange={setTaskVisibility}
                  selectedTaskId={selectedTaskId}
                  onSelectTask={selectTask}
                  missions={missions.missions}
                  missionsLoading={missions.loading}
                  onSelectMission={navigateToMission}
                  agents={agents}
                />
              )}
              {visibleTab === "plans" && (
                <div className="space-y-4">
                  {inDgPhase ? (
                    <SegmentedPillToggle
                      options={PLANS_VIEW_OPTIONS}
                      value={plansView}
                      onChange={setPlansView}
                      aria-label="Plans view"
                      data-testid="client-plans-view"
                      optionTestIdPrefix="client-plans-view"
                    />
                  ) : null}
                  {inDgPhase && plansView === "checklist" ? (
                    <DataGatheringSection
                      faClientId={faClientId}
                      summary={dgSummary}
                      onOpenTask={(taskId) => selectTask(taskId)}
                      activeTaskId={selectedTaskId}
                    />
                  ) : (
                    <PlansTab
                      faClientId={faClientId}
                      client={client}
                      onDelegatePlanTask={delegatePlanTask}
                    />
                  )}
                </div>
              )}
              {visibleTab === "calculators" && showCalculatorsTab && (
                <CalculatorsTab faClientId={faClientId} />
              )}
              {visibleTab === "artifacts" && (
                <ClientArtifactsSection faClientId={faClientId} />
              )}
              {visibleTab === "activity" && (
                timeline.loading ? (
                  <LoadingPlaceholder height="200px" />
                ) : (
                  <ClientTimeline entries={timeline.entries} />
                )
              )}
            </div>
          </>
        )}
      </div>

      {threadPaneTab && (
        <TaskThreadDrawer
          testId="client-task-thread"
          taskId={selectedTaskId}
          open={taskThreadOpen}
          onClose={closeTask}
          onTaskUpdated={() => {
            invalidateClientData();
          }}
          onOpenMission={openMissionById}
        />
      )}

    </div>
  );
}

// ── Header ───────────────────────────────────────────────────────────

function Header({
  client,
  onNewTask,
}: {
  client: FaClientDetail;
  onNewTask: () => void;
}) {
  const household = isHouseholdClient(client.type, client.member_count);
  const membersLine = memberNamesLine(client.members ?? []);
  const journeyCompact = compactJourneyStageLabel(client.journey_stage);
  const journeyFull = shortJourneyStageLabel(client.journey_stage);

  return (
    <div className="flex items-start gap-3 flex-wrap">
      <ClientAvatar name={client.display_name} size="md" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100 truncate">
            {client.display_name}
          </h1>
          {membersLine && (
            <span className="text-sm text-slate-400 truncate">
              · {membersLine}
            </span>
          )}
        </div>
        {journeyCompact ? (
          <div
            className="mt-1.5 flex items-center gap-1.5"
            title={journeyFull ?? undefined}
          >
            <StatusDot
              color={journeyStageStatusDotColor(client.journey_stage)}
              size="sm"
            />
            <Badge color={journeyStageBadgeColor(client.journey_stage)} size="sm">
              {journeyCompact}
            </Badge>
          </div>
        ) : null}
        {client.summary && (
          <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{client.summary}</p>
        )}
        <div className="flex items-center gap-2 flex-wrap mt-2">
          {household && (
            <Badge
              color="sky"
              size="sm"
              className="!normal-case !tracking-normal !font-sans"
            >
              Household · {client.member_count ?? client.members?.length ?? 0}{" "}
              members
            </Badge>
          )}
          {client.risk_profile && (
            <Badge
              color="amber"
              size="sm"
              className="!normal-case !tracking-normal !font-sans"
            >
              {client.risk_profile}
            </Badge>
          )}
          {client.tags.map((tag) => (
            <Badge
              key={tag}
              color="zinc"
              size="sm"
              className="!normal-case !tracking-normal !font-sans"
            >
              {tag}
            </Badge>
          ))}
        </div>
      </div>
      <Button
        color="indigo"
        onClick={onNewTask}
        data-testid="client-new-task"
        className="shrink-0 whitespace-nowrap"
      >
        <FontAwesomeIcon icon={faPlus} className="mr-2" />
        Delegate task
      </Button>
    </div>
  );
}

// ── Tabs ─────────────────────────────────────────────────────────────

function SectionHeading({
  children,
  action,
}: {
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {children}
      </h2>
      {action}
    </div>
  );
}

/** Task catalogue keys (see ani_api agentic_v2 seed). */
const CATALOGUE_GENERATE_LOAS = "octopus_generate_loas";
const CATALOGUE_PROCESS_PLAN = "octopus_process_plan";

const PLAN_SECTION_LABEL: Record<PlanCategory, string> = {
  pension: "Pensions",
  investment: "Investments",
  other: "Other plans",
};

function isPlanDgExcluded(dgStatus?: PlanDataGatheringStatus): boolean {
  return dgStatus?.excluded === true || dgStatus?.status === "excluded";
}

function countPlansInDgScope(
  plans: ClientPlanRow[],
  dgByPlan: Map<string, PlanDataGatheringStatus>,
  inDgPhase: boolean,
): number {
  if (!inDgPhase) return plans.length;
  return plans.filter(
    (plan) => !plan.plan_id || !isPlanDgExcluded(dgByPlan.get(plan.plan_id)),
  ).length;
}

const CLIENT_CARD_CLASS = "rounded-xl border border-slate-200 bg-white dark:border-white/10 dark:bg-white/5 dark:ring dark:ring-white/5 dark:ring-inset";

function DataGatheringSection({
  faClientId,
  summary,
  onOpenTask,
  activeTaskId,
}: {
  faClientId: string;
  summary: FaClientDataGathering | null | undefined;
  onOpenTask?: (taskId: string) => void;
  activeTaskId?: string | null;
}) {
  if (!summary) return null;

  return (
    <section data-testid="client-data-gathering">
      <div className={clsx(CLIENT_CARD_CLASS, "p-5")}>
      {summary.checklist?.data?.overall_summary ? (
        <DataGatheringAgentSummary summary={summary.checklist.data.overall_summary} />
      ) : null}

      {summary.checklist?.data?.overall_summary ? (
        <div className="my-5 border-t border-slate-200 dark:border-white/10" />
      ) : null}

      <DataGatheringPlanProgress summary={summary} />

      <div className="my-5 border-t border-slate-200 dark:border-white/10" />
      <DataGatheringClientPlansPanel
        faClientId={faClientId}
        summary={summary}
        variant="checklist"
        checklistSectionTitle="Checklist"
        showChecklistProgressBar
        showChecklistAgentSummary={false}
        wrapInCard={false}
        onOpenTask={onOpenTask}
        activeTaskId={activeTaskId}
      />
      </div>
    </section>
  );
}

function PlansTab({
  faClientId,
  client,
  onDelegatePlanTask,
}: {
  faClientId: string;
  client: FaClientDetail | null;
  onDelegatePlanTask: (
    catalogueId: string,
    inputsResolved: DelegatedTaskInputs,
  ) => void;
}) {
  const openWindow = useOpenWindow();
  const { artifacts, loading: artifactsLoading } = useClientArtifacts({
    faClientId,
    enabled: true,
    includeArchived: true,
  });
  const { summary: dgSummary } = useClientDataGathering({
    faClientId,
    enabled: true,
  });
  const artifactIndex = useMemo(
    () => buildPlanArtifactIndex(artifacts),
    [artifacts],
  );
  const artifactsById = useMemo(
    () => buildArtifactById(artifacts),
    [artifacts],
  );
  const [otherExpanded, setOtherExpanded] = useState(false);

  const plans = useMemo(
    () => (client?.plans as ClientPlanRow[] | undefined) ?? [],
    [client?.plans],
  );
  const members = client?.members ?? [];
  const grouped = useMemo(() => groupPlansByCategory(plans), [plans]);
  const dgByPlan = useMemo(() => {
    const map = new Map<string, PlanDataGatheringStatus>();
    for (const row of dgSummary?.plans ?? []) {
      map.set(row.plan_uuid, row);
    }
    return map;
  }, [dgSummary]);

  const onOpenPlanArtifactDocument = useCallback(
    (artifact: Artifact) => {
      openWindow({
        appId: "artifact",
        title: artifact.filename || "Document",
        params: { id: artifact._id },
      });
    },
    [openWindow],
  );

  const onOpenPlanArtifactDocumentEdit = useCallback(
    (artifact: Artifact) => {
      openWindow({
        appId: "artifact",
        title: artifact.filename || "Document",
        params: { id: artifact._id, mode: "edit" },
      });
    },
    [openWindow],
  );

  const onOpenPlanReportBuilder = useCallback(
    (artifact: Artifact) => {
      const reportId = artifact.storage?.report_builder?.report_id;
      if (!reportId) return;
      openWindow({
        appId: "report-builder",
        title: artifact.filename || "Report Builder",
        params: { report_id: reportId },
      });
    },
    [openWindow],
  );

  if (plans.length === 0) {
    return (
      <div className={clsx(CLIENT_CARD_CLASS, "p-6 text-sm text-slate-500 dark:text-slate-400")}>
        No plans on file. Once Plannr syncs the client fact-find,
        their plans will show up here and you can delegate LOA or cheat
        sheet tasks per plan.
      </div>
    );
  }

  const renderSection = (category: PlanCategory, sectionPlans: ClientPlanRow[]) => {
    if (sectionPlans.length === 0) return null;
    const inDgPhase = dgSummary?.in_data_gathering_phase ?? false;
    const sectionCount = countPlansInDgScope(sectionPlans, dgByPlan, inDgPhase);
    const sectionTotal = sumPlanValues(sectionPlans);
    const list = (
      <PlanList
        category={category}
        plans={sectionPlans}
        members={members}
        artifactIndex={artifactIndex}
        artifactsById={artifactsById}
        artifactsLoading={artifactsLoading}
        dgByPlan={dgByPlan}
        onDelegatePlanTask={onDelegatePlanTask}
        onOpenPlanArtifactDocument={onOpenPlanArtifactDocument}
        onOpenPlanArtifactDocumentEdit={onOpenPlanArtifactDocumentEdit}
        onOpenPlanReportBuilder={onOpenPlanReportBuilder}
      />
    );

    if (category === "other") {
      return (
        <Accordion
          key={category}
          isExpanded={otherExpanded}
          onToggle={() => setOtherExpanded((v) => !v)}
          className="border-slate-200 dark:border-white/10 !rounded-xl overflow-hidden"
          headerClassName="!bg-white dark:!bg-white/5 !px-4 !py-3 hover:!bg-slate-50 dark:hover:!bg-white/[0.07]"
          contentClassName="!p-0 !border-slate-200 dark:!border-white/5"
          header={
            <div className="flex w-full items-baseline justify-between gap-3">
              <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                {PLAN_SECTION_LABEL[category]} ({sectionCount})
              </span>
              {sectionTotal ? (
                <span className="text-xs font-medium tabular-nums text-slate-500">
                  {sectionTotal}
                </span>
              ) : null}
            </div>
          }
        >
          {list}
        </Accordion>
      );
    }

    return (
      <section key={category}>
        <div className="mb-2 flex items-baseline justify-between gap-3">
          <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {PLAN_SECTION_LABEL[category]} ({sectionCount})
          </h3>
          {sectionTotal ? (
            <span className="text-xs font-medium tabular-nums text-slate-500">
              {sectionTotal}
            </span>
          ) : null}
        </div>
        {list}
      </section>
    );
  };

  return (
    <div className="space-y-5">
      {renderSection("pension", grouped.pension)}
      {renderSection("investment", grouped.investment)}
      {renderSection("other", grouped.other)}
    </div>
  );
}

function PlanList({
  category,
  plans,
  members,
  artifactIndex,
  artifactsById,
  artifactsLoading,
  dgByPlan,
  onDelegatePlanTask,
  onOpenPlanArtifactDocument,
  onOpenPlanArtifactDocumentEdit,
  onOpenPlanReportBuilder,
}: {
  category: PlanCategory;
  plans: ClientPlanRow[];
  members: NonNullable<FaClientDetail["members"]>;
  artifactIndex: Map<string, PlanArtifactLinks>;
  artifactsById: Map<string, Artifact>;
  artifactsLoading: boolean;
  dgByPlan: Map<string, PlanDataGatheringStatus>;
  onDelegatePlanTask: (
    catalogueId: string,
    inputsResolved: DelegatedTaskInputs,
  ) => void;
  onOpenPlanArtifactDocument: (artifact: Artifact) => void;
  onOpenPlanArtifactDocumentEdit: (artifact: Artifact) => void;
  onOpenPlanReportBuilder: (artifact: Artifact) => void;
}) {
  return (
    <div className={clsx(CLIENT_CARD_CLASS, "divide-y divide-slate-200 dark:divide-white/5 overflow-hidden")}>
      {plans.map((plan, idx) => (
        <PlanRow
          key={plan.plan_id ?? `plan-${idx}`}
          category={category}
          plan={plan}
          members={members}
          links={plan.plan_id ? artifactIndex.get(plan.plan_id) : undefined}
          artifactsById={artifactsById}
          dgStatus={plan.plan_id ? dgByPlan.get(plan.plan_id) : undefined}
          artifactsLoading={artifactsLoading}
          onDelegatePlanTask={onDelegatePlanTask}
          onOpenPlanArtifactDocument={onOpenPlanArtifactDocument}
          onOpenPlanArtifactDocumentEdit={onOpenPlanArtifactDocumentEdit}
          onOpenPlanReportBuilder={onOpenPlanReportBuilder}
        />
      ))}
    </div>
  );
}

function PlanRowHeader({
  providerLabel,
  planName,
  metaLine,
  valueLabel,
  excluded,
}: {
  providerLabel: string | null;
  planName?: string;
  metaLine: string;
  valueLabel: string | null;
  excluded?: boolean;
}) {
  return (
    <div className="grid w-full grid-cols-[minmax(0,1fr)_6.5rem] items-start gap-3">
      <div className="min-w-0 text-left">
        <div
          className={clsx(
            "truncate text-sm font-semibold",
            excluded ? "text-slate-500" : "text-slate-900 dark:text-white",
          )}
        >
          {providerLabel ?? "Unknown provider"}
        </div>
        {planName ? (
          <div className="truncate text-xs text-slate-500 dark:text-slate-400">{planName}</div>
        ) : null}
        <p className="mt-0.5 text-xs capitalize text-slate-500 dark:text-slate-400">{metaLine}</p>
      </div>
      <div className="text-right text-sm font-medium tabular-nums text-slate-700 dark:text-slate-200">
        {valueLabel ?? "—"}
      </div>
    </div>
  );
}

function PlanRowActions({
  category,
  plan,
  keyId,
  hasPlanId,
  artifactsLoading,
  loaArtifact,
  cheatSheetArtifact,
  onDelegatePlanTask,
  onOpenPlanArtifactDocument,
  onOpenPlanArtifactDocumentEdit,
  onOpenPlanReportBuilder,
}: {
  category: PlanCategory;
  plan: ClientPlanRow;
  keyId: string;
  hasPlanId: boolean;
  artifactsLoading: boolean;
  loaArtifact?: Artifact;
  cheatSheetArtifact?: Artifact;
  onDelegatePlanTask: (
    catalogueId: string,
    inputsResolved: DelegatedTaskInputs,
  ) => void;
  onOpenPlanArtifactDocument: (artifact: Artifact) => void;
  onOpenPlanArtifactDocumentEdit: (artifact: Artifact) => void;
  onOpenPlanReportBuilder: (artifact: Artifact) => void;
}) {
  const showLoa = showPlanLoaStatus(category);

  return (
    <div className="border-t border-slate-200 bg-slate-50 px-4 py-2.5 dark:border-white/5 dark:bg-white/[0.02]">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {showLoa ? (
          <>
            <span className="w-10 shrink-0 text-[11px] font-medium uppercase tracking-wide text-slate-500">
              LOA
            </span>
            <PlanArtifactActions
              inline
              role="loa"
              groupLabel="LOA"
              artifact={loaArtifact}
              generateLabel="Generate LOA"
              actionLabels={{
                preview: "Preview",
                edit: "Edit",
              }}
              disabled={!hasPlanId || artifactsLoading}
              onGenerate={() =>
                onDelegatePlanTask(CATALOGUE_GENERATE_LOAS, buildGenerateLoaInputs(plan))
              }
              onOpenDocument={onOpenPlanArtifactDocument}
              onOpenDocumentEdit={onOpenPlanArtifactDocumentEdit}
              onOpenReportBuilder={onOpenPlanReportBuilder}
              testId={`plan-loa-${keyId}`}
            />
            <span className="hidden h-4 w-px bg-slate-200 dark:bg-white/10 sm:block" aria-hidden />
          </>
        ) : null}
        <span className="w-[4.5rem] shrink-0 text-[11px] font-medium uppercase tracking-wide text-slate-500">
          Sheet
        </span>
        <PlanArtifactActions
          inline
          role="cheatSheet"
          groupLabel="Cheat sheet"
          artifact={cheatSheetArtifact}
          generateLabel="Generate"
          actionLabels={{
            view: "View",
            edit: "Edit",
          }}
          disabled={!hasPlanId || artifactsLoading}
          onGenerate={() =>
            onDelegatePlanTask(CATALOGUE_PROCESS_PLAN, buildProcessPlanInputs(plan))
          }
          onOpenDocument={onOpenPlanArtifactDocument}
          onOpenDocumentEdit={onOpenPlanArtifactDocumentEdit}
          onOpenReportBuilder={onOpenPlanReportBuilder}
          testId={`plan-cheat-sheet-${keyId}`}
        />
      </div>
    </div>
  );
}

function PlanRow({
  category,
  plan,
  members,
  links,
  artifactsById,
  dgStatus,
  artifactsLoading,
  onDelegatePlanTask,
  onOpenPlanArtifactDocument,
  onOpenPlanArtifactDocumentEdit,
  onOpenPlanReportBuilder,
}: {
  category: PlanCategory;
  plan: ClientPlanRow;
  members: NonNullable<FaClientDetail["members"]>;
  links?: PlanArtifactLinks;
  artifactsById: Map<string, Artifact>;
  dgStatus?: PlanDataGatheringStatus;
  artifactsLoading: boolean;
  onDelegatePlanTask: (
    catalogueId: string,
    inputsResolved: DelegatedTaskInputs,
  ) => void;
  onOpenPlanArtifactDocument: (artifact: Artifact) => void;
  onOpenPlanArtifactDocumentEdit: (artifact: Artifact) => void;
  onOpenPlanReportBuilder: (artifact: Artifact) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const key = plan.plan_id ?? "unknown";
  const owners = planOwnersLabel(plan, members);
  const hasPlanId = !!plan.plan_id;
  const valueLabel = formatPlanValue(plan.value);
  const loaArtifact = links?.loa;
  const cheatSheetArtifact = resolvePlanCheatSheetArtifact(
    plan.plan_id,
    links,
    dgStatus?.cheat_sheet_artifact_id,
    artifactsById,
  );
  const providerLabel = renderPlanField(plan.provider);
  const planName = plan.name?.trim();
  const excluded = isPlanDgExcluded(dgStatus);
  const metaLine = [
    renderPlanField(plan.type),
    owners ?? (plan.ownership ? plan.ownership : null),
    renderPlanField(plan.policy_number)
      ? `Policy ${renderPlanField(plan.policy_number)}`
      : null,
    excluded && dgStatus?.exclusion_reason ? dgStatus.exclusion_reason : null,
  ]
    .filter(Boolean)
    .join(" · ");

  if (excluded) {
    return (
      <div
        className="px-4 py-3 opacity-45"
        data-testid={`plan-row-excluded-${key}`}
      >
        <PlanRowHeader
          providerLabel={providerLabel}
          planName={planName}
          metaLine={metaLine}
          valueLabel={valueLabel}
          excluded
        />
      </div>
    );
  }

  return (
    <div data-testid={`plan-row-${key}`}>
      <Accordion
        isExpanded={expanded}
        onToggle={() => setExpanded((v) => !v)}
        className="border-0 !rounded-none ring-0 overflow-hidden"
        headerClassName="!bg-transparent !px-4 !py-3 hover:!bg-slate-50 dark:hover:!bg-white/[0.04]"
        contentClassName="!p-0 !border-slate-200 dark:!border-white/5"
        header={
          <PlanRowHeader
            providerLabel={providerLabel}
            planName={planName}
            metaLine={metaLine}
            valueLabel={valueLabel}
          />
        }
      >
        <PlanRowActions
          category={category}
          plan={plan}
          keyId={key}
          hasPlanId={hasPlanId}
          artifactsLoading={artifactsLoading}
          loaArtifact={loaArtifact}
          cheatSheetArtifact={cheatSheetArtifact}
          onDelegatePlanTask={onDelegatePlanTask}
          onOpenPlanArtifactDocument={onOpenPlanArtifactDocument}
          onOpenPlanArtifactDocumentEdit={onOpenPlanArtifactDocumentEdit}
          onOpenPlanReportBuilder={onOpenPlanReportBuilder}
        />
      </Accordion>
    </div>
  );
}

function buildGenerateLoaInputs(plan: ClientPlanRow): DelegatedTaskInputs {
  return {
    loa_scope: "single",
    plan_id: plan.plan_id,
  };
}

function buildProcessPlanInputs(plan: ClientPlanRow): DelegatedTaskInputs {
  const planType = renderPlanField(plan.type);
  const provider = renderPlanField(plan.provider);
  const planName =
    provider && planType
      ? `${provider} ${planType}`
      : provider ?? planType ?? undefined;
  return {
    plan_uuid: plan.plan_id,
    reason: "client_plans_tab",
    ...(planType ? { plan_type: planType } : {}),
    ...(provider ? { provider } : {}),
    ...(planName ? { plan_name: planName } : {}),
  };
}

function CalculatorsTab({ faClientId }: { faClientId: string }) {
  return <ClientCalculatorLinks faClientId={faClientId} variant="tab" />;
}

function OverviewTab({
  client,
  faClientId,
  missionId,
  needsYouCount,
  tasksLoading,
  showCalculators,
  onReviewTasks,
}: {
  client: FaClientDetail;
  faClientId: string;
  missionId: string | null;
  needsYouCount: number;
  tasksLoading: boolean;
  showCalculators: boolean;
  onReviewTasks: () => void;
}) {
  const members = client.members ?? [];

  return (
    <div className="space-y-6">
      <section
        className={clsx(CLIENT_CARD_CLASS, "flex flex-wrap items-center justify-between gap-3 px-4 py-3")}
        data-testid="client-overview-needs-you"
      >
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
            {tasksLoading
              ? "Checking tasks…"
              : needsYouCount === 0
                ? "Nothing needs you for this client"
                : `${needsYouCount} task${needsYouCount === 1 ? "" : "s"} need${needsYouCount === 1 ? "s" : ""} you`}
          </p>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">Reviews and blocked steps waiting on a person.</p>
        </div>
        {needsYouCount > 0 ? (
          <Button color="indigo" onClick={onReviewTasks} data-testid="client-overview-review-tasks">
            Review tasks
          </Button>
        ) : null}
      </section>

      {showCalculators ? <ClientCalculatorLinks faClientId={faClientId} variant="card" /> : null}

      <ClientNarrativeSourcePanel faClientId={faClientId} missionId={missionId} />

      <ClientInsightCard faClientId={faClientId} enabled />

      {members.length > 0 && (
        <section>
          <SectionHeading>Household members</SectionHeading>
          <ul className={clsx(CLIENT_CARD_CLASS, "divide-y divide-slate-200 dark:divide-white/5")}>
            {members.map((member) => (
              <MemberRow key={member.id} member={member} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function MemberRow({ member }: { member: NonNullable<FaClientDetail["members"]>[number] }) {
  const email = member.contact?.email as string | undefined;
  const phone = member.contact?.phone as string | undefined;

  return (
    <li className="px-4 py-3 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-900 dark:text-white">
          {memberDisplayName(member)}
        </div>
        <div className="text-[11px] text-slate-500 capitalize mt-0.5">
          {member.role}
          {member.risk_profile ? ` · ${member.risk_profile} risk` : ""}
        </div>
      </div>
      <div className="text-right text-xs text-slate-500 dark:text-slate-400 shrink-0 max-w-[220px]">
        {email && <div className="truncate">{email}</div>}
        {phone && <div className="truncate mt-0.5">{phone}</div>}
      </div>
    </li>
  );
}

function MissionsTab({
  missions,
  loading,
  onSelect,
  agents,
}: {
  missions: MissionSummary[];
  loading: boolean;
  onSelect: (m: MissionSummary) => void;
  agents: AgentSummary[];
}) {
  const {
    ref: scrollRef,
    element: scrollElement,
    scrollReady,
  } = useVirtualScrollElement<HTMLDivElement>();

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 items-center justify-between mb-4">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {loading
            ? "Loading missions..."
            : missions.length === 0
              ? "No missions for this client yet."
              : `${missions.length} mission${missions.length === 1 ? "" : "s"}`}
        </p>
      </div>
      {loading ? (
        <div className="space-y-2">
          <LoadingPlaceholder height="56px" />
          <LoadingPlaceholder height="56px" />
        </div>
      ) : missions.length === 0 ? null : (
        <div ref={scrollRef} className="-mx-2 flex-1 min-h-0 overflow-y-auto">
          <MissionsVirtualList
            missions={missions}
            agents={agents}
            refreshing={false}
            onMissionClick={onSelect}
            scrollElement={scrollElement}
            scrollReady={scrollReady}
          />
        </div>
      )}
    </div>
  );
}

function TasksTab({
  view,
  onViewChange,
  tasks,
  needsYouTasks,
  loading,
  visibility,
  onVisibilityChange,
  selectedTaskId,
  onSelectTask,
  missions,
  missionsLoading,
  onSelectMission,
  agents,
}: {
  view: TaskView;
  onViewChange: (view: TaskView) => void;
  tasks: TaskActionItem[];
  needsYouTasks: TaskActionItem[];
  loading: boolean;
  visibility: VisibilityScope;
  onVisibilityChange: (v: VisibilityScope) => void;
  selectedTaskId: string | null;
  onSelectTask: (id: string) => void;
  missions: MissionSummary[];
  missionsLoading: boolean;
  onSelectMission: (m: MissionSummary) => void;
  agents: AgentSummary[];
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 mb-4">
        <SegmentedPillToggle
          options={TASK_VIEW_OPTIONS}
          value={view}
          onChange={onViewChange}
          aria-label="Task view"
          data-testid="client-task-view"
          optionTestIdPrefix="client-task-view"
        />
        {view !== "missions" ? <VisibilityFilter value={visibility} onChange={onVisibilityChange} /> : null}
      </div>
      {view === "missions" ? (
        <MissionsTab missions={missions} loading={missionsLoading} onSelect={onSelectMission} agents={agents} />
      ) : loading ? (
        <div className="space-y-2">
          <LoadingPlaceholder height="56px" />
          <LoadingPlaceholder height="56px" />
        </div>
      ) : view === "needs_you" ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <TaskReviewTable tasks={needsYouTasks} loading={loading} emptyMessage="Nothing needs you for this client." testId="client-task-review-table" onOpenThread={onSelectTask} />
        </div>
      ) : tasks.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">No tasks for this client yet.</p>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          <ClientTasksKanban
            tasks={tasks}
            selectedTaskId={selectedTaskId}
            onSelectTask={onSelectTask}
          />
        </div>
      )}
    </div>
  );
}

// ── Error state ──────────────────────────────────────────────────────

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-slate-300 py-12 gap-3">
      <FontAwesomeIcon
        icon={faExclamationTriangle}
        className="text-amber-500 text-2xl"
      />
      <p className="text-sm">{message}</p>
      <Button onClick={onRetry}>Retry</Button>
    </div>
  );
}
